import Avatar from "../../../componentes/ui/Avatar";
import verifiedBadge from "../../../assets/verified.png";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { InlineSpinner } from "../../../componentes/LoadingIndicator";
import { formatDate } from "../../../utils/dateFormat";

const defaultRenderStats = () => [];

// Match BlogList helpers for stripping and excerpts so behavior is identical.
const stripHtml = (html) => {
  if (!html) return "";
  const doc = new DOMParser().parseFromString(html, "text/html");
  return doc.body.textContent || "";
};

const getExcerptFromHtml = (html, maxLength = 160) => {
  const text = stripHtml(html).replace(/\s+/g, " ").trim();
  if (!text) return "";
  if (text.length <= maxLength) return text;
  return `${text.slice(0, maxLength).trimEnd()}…`;
};

const defaultGetExcerpt = (item) => {
  if (typeof item?.excerpt === "string" && item.excerpt.trim().length > 0) {
    return getExcerptFromHtml(item.excerpt, 160);
  }
  return getExcerptFromHtml(item?.content || "", 160);
};

const coverImageKeys = [
  "hero_image",
  "cover_image",
  "cover_image_url",
  "image",
  "image_url",
  "thumbnail",
  "banner",
];

// Match BlogList image resolution: prefer cover/image fields,
// then fall back to first <img> inside the HTML content.
const getFirstImageFromHtml = (html) => {
  if (!html) return null;
  const doc = new DOMParser().parseFromString(html, "text/html");
  const img = doc.querySelector("img");
  return img ? img.src : null;
};

const resolveCoverImage = (item) => {
  for (const key of coverImageKeys) {
    const value = item?.[key];
    if (typeof value === "string" && value.trim()) {
      return value.trim();
    }
  }
  const fromContent = getFirstImageFromHtml(item?.content);
  if (fromContent) {
    return fromContent;
  }
  return null;
};

const resolveAuthorAvatar = (item, authorMeta) => {
  const candidateOrder = [
    authorMeta?.avatar_url,
    authorMeta?.avatar,
    authorMeta?.profile?.avatar_url,
    authorMeta?.profile?.avatar,
    item?.author_avatar,
    item?.author_image,
  ];
  for (const candidate of candidateOrder) {
    if (typeof candidate === "string" && candidate.trim()) {
      return candidate.trim();
    }
  }
  return null;
};

const getInitials = (text) => {
  if (typeof text !== "string" || !text.trim()) {
    return "";
  }
  return text
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((segment) => segment.charAt(0).toUpperCase())
    .join("");
};

const getAuthorDisplayName = (authorMeta) => {
  if (!authorMeta) return "";
  const rawFirstName =
    authorMeta?.first_name || authorMeta?.profile?.first_name || "";
  const rawLastName =
    authorMeta?.last_name || authorMeta?.profile?.last_name || "";
  const fullName = [rawFirstName, rawLastName].filter(Boolean).join(" ").trim();
  return (
    fullName ||
    authorMeta?.name ||
    authorMeta?.display_name ||
    authorMeta?.username ||
    ""
  );
};

const gradientPalette = [
  "from-sky-400 via-cyan-300 to-blue-500",
  "from-rose-400 via-pink-300 to-orange-300",
  "from-emerald-400 via-teal-300 to-lime-300",
  "from-violet-500 via-indigo-400 to-blue-400",
  "from-amber-400 via-yellow-300 to-orange-400",
  "from-fuchsia-500 via-pink-400 to-rose-400",
];

export default function MoreFromAuthor({
  authorName,
  items = [],
  loading = false,
  profileLink,
  headerPrefix = "More from",
  viewProfileLabel = "View profile",
  loadingMessage = "Loading entries...",
  emptyMessage = "No additional entries yet.",
  hideWhenEmpty = false,
  description,
  showMedia = true,
  buildItemLink,
  getExcerpt = defaultGetExcerpt,
  renderStats = defaultRenderStats,
  getDateLabel,
}) {
  const resolvedItems = Array.isArray(items) ? items : [];
  const limitedItems = resolvedItems.slice(0, 4);
  const resolveLink = typeof buildItemLink === "function" ? buildItemLink : () => "#";
  const resolveDate = typeof getDateLabel === "function"
    ? getDateLabel
    : (item) => item?.created_at || item?.createdAt ? formatDate(item.created_at || item.createdAt) : "Recently";

  if (hideWhenEmpty && !loading && resolvedItems.length === 0) {
    return null;
  }

  const resolvedDescription =
    description || `Fresh entries from ${authorName || "this author"}.`;

  return (
    <section className="mt-4">
      <div className="relative overflow-hidden rounded-3xl border border-gray-100 dark:border-gray-800 bg-gradient-to-br from-white via-white to-slate-50 dark:from-gray-900 dark:via-gray-900 dark:to-gray-800 shadow-[0_20px_45px_rgba(15,23,42,0.08)]">
        <div className="relative z-10 px-6 py-8 sm:px-10 sm:py-12">
          <div className="flex flex-col gap-6 md:flex-row md:items-center md:justify-between">
            <div className="text-center md:text-left">
              <p className="text-xs font-semibold uppercase tracking-[0.2em] text-primary/80">
                {headerPrefix}
              </p>
              <h3 className="mt-2 text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center md:justify-start gap-2">
                {authorName}
                {items[0]?.author?.is_trusted || items[0]?.author?.status?.is_trusted ? (
                  <img
                    src={verifiedBadge}
                    alt="Verified"
                    className="w-6 h-6 shrink-0 drop-shadow-sm translate-y-[1px]"
                  />
                ) : null}
              </h3>
              <p className="mt-3 text-base text-gray-600 dark:text-gray-300">
                {resolvedDescription}
              </p>
            </div>
            {profileLink && (
              <Link
                to={profileLink}
                className="inline-flex items-center justify-center gap-2 rounded-full border border-primary/30 bg-white/90 px-5 py-2.5 text-sm font-semibold text-primary shadow-sm backdrop-blur hover:bg-primary hover:text-white dark:bg-gray-900/70"
              >
                {viewProfileLabel}
                <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            )}
          </div>

          {loading ? (
            <div className="mt-12 flex justify-center">
              <InlineSpinner tone="slate" message={loadingMessage} />
            </div>
          ) : limitedItems.length > 0 ? (
            <div className="mt-12 grid gap-6 sm:grid-cols-2">
              {limitedItems.map((item, index) => {
                const stats = (renderStats(item) || []).filter(Boolean).slice(0, 3);
                const excerpt = getExcerpt(item);
                const dateLabel = resolveDate(item);
                const key = item.id || item.slug || item.title || index;
                const coverImage = resolveCoverImage(item);
                const authorMeta = item.author || {};
                const avatar = resolveAuthorAvatar(item, authorMeta);
                const authorLabel = getAuthorDisplayName(authorMeta) || authorName;
                const authorInitials = getInitials(authorLabel) || "CM";
                const gradient = gradientPalette[index % gradientPalette.length];
                const topPaddingClass = showMedia ? "pt-4" : "pt-6";

                return (
                  <Link
                    key={key}
                    to={resolveLink(item)}
                    className="group flex flex-col overflow-hidden rounded-3xl border border-gray-100 bg-white/90 shadow-lg transition duration-300 hover:-translate-y-1 hover:shadow-2xl dark:border-gray-800 dark:bg-gray-900/80"
                  >
                    {showMedia && (
                      <div className="relative mx-4 mt-4 h-48 overflow-hidden rounded-2xl">
                        {coverImage ? (
                          <img
                            src={coverImage}
                            alt={item.title || "Author entry"}
                            className="h-full w-full object-cover transition duration-500 group-hover:scale-105"
                            loading="lazy"
                          />
                        ) : (
                          <div className={`flex h-full w-full items-center justify-center bg-gradient-to-br ${gradient}`}>
                            <svg
                              className="h-16 w-16 text-white/40"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9a2 2 0 00-2-2h-2m-4-3H9M7 16h6M7 8h6v4H7V8z"
                              />
                            </svg>
                          </div>
                        )}
                        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/60 via-black/0 to-transparent" />
                      </div>
                    )}

                    <div className={`flex items-center gap-3 px-6 ${topPaddingClass}`}>
                      <Avatar
                        src={avatar}
                        name={authorLabel}
                        size={12}
                      />
                      <div className="min-w-0">
                        <p className="text-sm font-semibold text-gray-900 dark:text-white flex items-center gap-1.5">
                          {authorLabel}
                          {(authorMeta?.is_trusted || authorMeta?.status?.is_trusted) && (
                            <img
                              src={verifiedBadge}
                              alt="Verified"
                              className="w-3.5 h-3.5 shrink-0 drop-shadow-sm translate-y-[0.5px]"
                            />
                          )}
                        </p>
                        <p className="text-xs text-gray-500 dark:text-gray-400">{dateLabel}</p>
                      </div>
                    </div>

                    <div className={`flex flex-1 flex-col px-6 pb-6 ${topPaddingClass}`}>
                      <h4 className="text-lg font-semibold text-gray-900 transition group-hover:text-primary dark:text-white">
                        {item.title || "Untitled entry"}
                      </h4>
                      {excerpt && (
                        <p className="mt-2 text-sm text-gray-600 line-clamp-3 dark:text-gray-300">
                          {excerpt}
                        </p>
                      )}
                      {stats.length > 0 && (
                        <div className="mt-4 flex flex-wrap gap-2 text-xs text-gray-500 dark:text-gray-400">
                          {stats.map((stat, statIndex) => (
                            <span
                              key={statIndex}
                              className="rounded-full bg-gray-100 px-3 py-1 text-[0.7rem] font-medium text-gray-600 transition group-hover:bg-primary/10 group-hover:text-primary dark:bg-gray-800 dark:text-gray-300"
                            >
                              {stat}
                            </span>
                          ))}
                        </div>
                      )}
                    </div>
                  </Link>
                );
              })}
            </div>
          ) : (
            <div className="mt-10 rounded-2xl border border-dashed border-gray-200 bg-white/60 px-6 py-10 text-center text-gray-500 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-400">
              {emptyMessage}
            </div>
          )}
        </div>

        <div className="pointer-events-none absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-white to-transparent opacity-70 dark:from-black" />
      </div>
    </section>
  );
}

MoreFromAuthor.propTypes = {
  authorName: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.object),
  loading: PropTypes.bool,
  profileLink: PropTypes.string,
  headerPrefix: PropTypes.string,
  viewProfileLabel: PropTypes.string,
  loadingMessage: PropTypes.string,
  emptyMessage: PropTypes.string,
  hideWhenEmpty: PropTypes.bool,
  description: PropTypes.string,
  showMedia: PropTypes.bool,
  buildItemLink: PropTypes.func,
  getExcerpt: PropTypes.func,
  renderStats: PropTypes.func,
  getDateLabel: PropTypes.func,
};
